/**
 * CODE EDITOR COMPONENT
 * AI-powered code editor with real-time assistance
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Play, Save, Sparkles, Brain } from 'lucide-react';

interface CodeEditorProps {
  sessionId: string;
  sendMessage: (message: any) => void;
}

export function CodeEditor({ sessionId, sendMessage }: CodeEditorProps) {
  const [code, setCode] = useState(`// Welcome to CodeMind AI Code Editor
// Powered by Llama 3.3 for intelligent assistance

function greetUser(name) {
  return \`Hello, \${name}! Welcome to the future of coding.\`;
}

// Try asking the AI to:
// - Optimize this function
// - Add error handling
// - Convert to TypeScript
// - Add unit tests

console.log(greetUser("Developer"));`);

  const [language, setLanguage] = useState('javascript');

  const handleAnalyze = async () => {
    try {
      const response = await fetch('https://codemind-ai.codeworker.workers.dev/api/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code,
          language,
          analysisType: 'comprehensive'
        })
      });
      
      const data = await response.json();
      if (data.success) {
        // Analysis completed - could update UI with results
        console.log('Analysis completed:', data.analysis);
      }
    } catch (error) {
      console.error('Analysis failed:', error);
    }
  };

  const handleRun = () => {
    // Simulate code execution
    console.log('Running code:', code);
    alert('Code execution simulated - check console for output');
  };

  const handleSave = () => {
    // Save code to localStorage or send to backend
    localStorage.setItem('codemind-code', code);
    localStorage.setItem('codemind-language', language);
    alert('Code saved successfully!');
  };

  return (
    <div className="h-full flex flex-col bg-gradient-to-b from-slate-900/50 to-slate-800/50">
      {/* Editor Header */}
      <div className="border-b border-white/10 p-4 bg-white/5">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h2 className="text-lg font-semibold text-white flex items-center space-x-2">
              <Brain className="w-5 h-5 text-blue-400" />
              <span>AI Code Editor</span>
            </h2>
            
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="bg-white/10 border border-white/20 rounded px-3 py-1 text-white text-sm"
            >
              <option value="javascript">JavaScript</option>
              <option value="typescript">TypeScript</option>
              <option value="python">Python</option>
              <option value="go">Go</option>
              <option value="rust">Rust</option>
            </select>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={handleAnalyze}
              className="flex items-center space-x-2 px-3 py-2 bg-gradient-to-r from-purple-500 to-pink-600 text-white rounded-lg hover:from-purple-600 hover:to-pink-700 transition-all"
            >
              <Sparkles className="w-4 h-4" />
              <span>AI Analyze</span>
            </button>
            
            <button 
              onClick={handleRun}
              className="flex items-center space-x-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Play className="w-4 h-4" />
              <span>Run</span>
            </button>
            
            <button 
              onClick={handleSave}
              className="flex items-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Save className="w-4 h-4" />
              <span>Save</span>
            </button>
          </div>
        </div>
      </div>

      {/* Editor Content */}
      <div className="flex-1 flex">
        {/* Code Area */}
        <div className="flex-1 p-4">
          <textarea
            value={code}
            onChange={(e) => setCode(e.target.value)}
            className="w-full h-full bg-slate-900 border border-slate-700 rounded-lg p-4 text-slate-200 font-mono text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Start coding... AI will assist you in real-time!"
          />
        </div>

        {/* AI Assistant Panel */}
        <div className="w-80 border-l border-white/10 bg-white/5 p-4">
          <h3 className="text-white font-medium mb-4 flex items-center space-x-2">
            <Sparkles className="w-4 h-4 text-yellow-400" />
            <span>AI Assistant</span>
          </h3>
          
          <div className="space-y-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white/10 border border-white/20 rounded-lg p-3"
            >
              <div className="text-sm text-white mb-2">💡 AI Suggestions:</div>
              <div className="text-xs text-slate-400 text-center py-4">
                Analyze code to see AI suggestions
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white/10 border border-white/20 rounded-lg p-3"
            >
              <div className="text-sm text-white mb-2">🔍 Code Analysis:</div>
              <div className="text-xs text-slate-400 text-center py-4">
                No analysis available yet
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white/10 border border-white/20 rounded-lg p-3"
            >
              <div className="text-sm text-white mb-2">🚀 Quick Actions:</div>
              <div className="text-xs text-slate-400 text-center py-4">
                Deploy to Cloudflare to enable AI actions
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}